import { useMemo } from "react";
import { type SearchResultRow, type SrpProductData } from "../../../../utils/models";
import { PredefinedTraits } from "../../../../ProductDetails/components/ProductTrait";
import { useSelector } from "react-redux";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "../../../../ProductComparison/ProductComparisonCheckbox";
import { type RootState } from "../../../../redux/store";
import { Card } from "../../../../Configurators/Linear/components/Card";
import {
  AliasesParameter,
  OldDesignationParameter,
  SchaefflerIdParameter,
  SearchPageParamContainer,
} from "../../../../common/components/Product/ProductParams";
import { SearchTechValueTable } from "./SearchTechValueTable";
import useMediaQuery from "../../../../common/hooks/useMediaQuery";
import { fakeDescriptorFromProduct } from "../../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { NewVersionMessage } from "../../../../common/components/Messages/NewVersionMessage";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../../../../utils/screenSizes";
import { useTranslation } from "react-i18next";
import { SRPPackageRestrictionContext } from "~/common/components/PackageRestrictionContext/SRPPackageRestrictionContext";
import { getGALocationForSearchItem, useSearchPageType } from "~/Search/useSearchPageType";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";
import { BasicAddToCartButton } from "~/feature/Product/elements/AddToCartButton";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import {
  CardContent,
  ProductDetailsLayout,
} from "~/Search/components/SearchResults/ListView/SearchResultItemCommonElements";
import { Product } from "~/feature/Product";
import classes from "~/Search/components/SearchResults/ListView/SearchResultProductCustomer.module.less";
import { Box, styled } from "@mui/material";
import { canSeeBuyFromSalesPartnerButton } from "~/feature/SalesPartnerStockIntegration/salesPartnerStockIntegrationHelper";
import { BuyFromSalesPartnerButton } from "~/feature/SalesPartnerStockIntegration/components/BuyFromSalesPartnerButton";
import SrpAddToWishlist from "~/Search/components/SearchResults/ListView/SrpAddToWishlist";
import { useResultTileZoovuParams } from "~/Search/utils/searchUtils";
import { getPurchaseDataFromUrl } from "~/ProductDetails/components/ProductSummary/PdpSummaryHelper";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/icons/Cycle";
import parse from "html-react-parser";

interface Props {
  isPdp?: boolean;
  resultRow: SearchResultRow;
  index: number;
  onProductClick: (area: string, product: SrpProductData) => void;
}

const SRPProductDetailsLayout = styled(ProductDetailsLayout)(({ theme }) => ({
  [".buttons-slot"]: {
    [theme.breakpoints.up("desktop-s")]: {
      alignSelf: "stretch",
      gridRow: "1 / 3",
    },
  },

  [".product-data-slot"]: {
    [theme.breakpoints.up("desktop-s")]: {
      gridColumn: "2 / 4",
    },
  },
}));

export const SearchResultProductNonCustomer = ({ resultRow, onProductClick, isPdp }: Props) => {
  const { t } = useTranslation();

  const pageType = useSearchPageType();

  const gaLocation = getGALocationForSearchItem(isPdp, pageType);

  const { productCode, features } = resultRow;

  const product = useSelector((state: RootState) => state.products.basic[productCode]) as SrpProductData;

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const { productSelectClick, productInformationClick } = useMemo(
    () => ({
      productInformationClick: () => onProductClick("Product information", product),
      productSelectClick: () => onProductClick("Select", product),
    }),
    [onProductClick, product],
  );

  const descriptor = useMemo(() => fakeDescriptorFromProduct(product), [product]);

  const showAddToCart = canSeeAddToCartButton(true);
  const showBuyFromFromSalesPartner = canSeeBuyFromSalesPartnerButton();
  const showAddToWishlist = !showBuyFromFromSalesPartner && canSeeWishlistButton();

  const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
  const pdpLinkProps = {
    onClick: productInformationClick,
    reco_type: recommendationType,
    reco_place: recommendationPlace,
  };

  return (
    <SRPPackageRestrictionContext productCode={productCode}>
      <ProductDescriptorContext value={descriptor}>
        <Card
          className={"jsSearchResultProduct"}
          data-test-id={`search-result-item`}
          disableHover={true}
          {...useResultTileZoovuParams(product)}
        >
          <CardContent>
            <SRPProductDetailsLayout
              imageSlot={
                <Product.PdpLink {...pdpLinkProps}>
                  <Product.PrimaryImageWithLogos className={classes.image} clickable={false} />
                </Product.PdpLink>
              }
              productDataSlot={
                <div className={classes.dataCol}>
                  <Product.PdpLink {...pdpLinkProps}>
                    <Product.FullDesignation clickable={false} className={classes.designation} />
                  </Product.PdpLink>

                  <Product.PdpLink {...pdpLinkProps}>
                    <Product.ShortDescription clickable={false} />
                  </Product.PdpLink>

                  {product.reconditionable && (
                    <RichTooltip
                      chipText={t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
                      message={parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}
                      linkText={t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                      ChipIcon={<Cycle fontSize="small" />}
                      variant="success"
                    />
                  )}

                  <NewVersionMessage className={classes.newVersionMessage} baseProduct={product} />

                  <SearchPageParamContainer className={classes.paramContainer}>
                    <OldDesignationParameter variant={"search-page"} label={t("product.oldDesignations")} />
                    <SchaefflerIdParameter variant={"search-page"} />
                    <AliasesParameter variant={"search-page"} mode={"13-digits"} />
                  </SearchPageParamContainer>
                </div>
              }
              techTableSlot={<SearchTechValueTable className={classes.technicalTable} features={features} />}
              actionSlot={null}
              priceDataSlot={null}
              buttonsSlot={
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: isDesktop ? "flex-end" : "flex-start",
                    height: "100%",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 2,
                    }}
                  >
                    <div>{isDesktop && product.preferred && <PredefinedTraits.PreferredProduct />}</div>
                    {isProductComparisonCheckboxVisible(product) && (
                      <ProductComparisonCheckbox productCode={productCode} gaLocation={gaLocation} />
                    )}
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, width: "100%" }}>
                    {showBuyFromFromSalesPartner && (
                      <BuyFromSalesPartnerButton product={product} pageType={gaLocation} />
                    )}
                    {showAddToCart && (
                      <BasicAddToCartButton
                        onClick={productSelectClick}
                        color={showBuyFromFromSalesPartner ? "secondary" : "primary"}
                        fullWidth
                        wizardConfig={{ source: "srp" }}
                        reco_place={recommendationPlace}
                        reco_type={recommendationType}
                      />
                    )}
                    {showAddToWishlist && (
                      <SrpAddToWishlist product={product} color={showAddToCart ? "secondary" : "primary"} />
                    )}
                  </Box>
                </Box>
              }
            />
          </CardContent>
        </Card>
      </ProductDescriptorContext>
    </SRPPackageRestrictionContext>
  );
};
